import { cn } from "@/utils/cn";

export type LoadingProgressBarSize = "sm" | "md" | "lg";
export type LoadingProgressBarColor = "brand" | "white";

interface LoadingProgressBarProps {
  percent: number;
  size?: LoadingProgressBarSize;
  color?: LoadingProgressBarColor;
  showValue?: boolean;
  className?: string;
}

const trackByColor: Record<LoadingProgressBarColor, string> = {
  brand: "bg-slate-100",
  white: "bg-white/25",
};

const fillByColor: Record<LoadingProgressBarColor, string> = {
  brand: "bg-brand-600",
  white: "bg-white",
};

const valueByColor: Record<LoadingProgressBarColor, string> = {
  brand: "text-brand-700",
  white: "text-white",
};

const heightBySize: Record<LoadingProgressBarSize, string> = {
  sm: "h-1.5",
  md: "h-2",
  lg: "h-2.5",
};

export function LoadingProgressBar({
  percent,
  size = "md",
  color = "brand",
  showValue = true,
  className,
}: LoadingProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));

  return (
    <div
      className={cn("w-full min-w-[7.5rem]", className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      aria-label="Progresso do carregamento"
    >
      {showValue && (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              "text-[11px] font-medium",
              color === "white" ? "text-white/70" : "text-slate-400",
            )}
          >
            Progresso
          </span>
          <span className={cn("text-sm font-bold tabular-nums", valueByColor[color])}>
            {clamped}%
          </span>
        </div>
      )}
      <div className={cn("w-full overflow-hidden rounded-full", trackByColor[color], heightBySize[size])}>
        <div
          className={cn("h-full rounded-full transition-[width] duration-150 ease-out", fillByColor[color])}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
